import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { EDIConfig } from './config';
import { setJWTClaims } from './config';

/**
 * Dynamic Supabase Client Manager
 * Supports runtime configuration for external authentication
 */

let _client: SupabaseClient | null = null;
let _currentConfig: EDIConfig | null = null;

/**
 * Initialize Supabase client with provided configuration
 * Can be called multiple times to reinitialize with new config
 */
export function initializeSupabase(config: EDIConfig): SupabaseClient {
  // Determine which key to use (JWT takes priority)
  const authKey = config.jwtToken || config.anonKey;
  
  if (!authKey) {
    throw new Error('Either JWT token or anonymous key is required');
  }
  
  console.log('🔐 Initializing Supabase client...', {
    url: config.supabaseUrl,
    authType: config.jwtToken ? 'JWT' : 'Anonymous Key',
  });
  
  // Create options for JWT authentication if JWT is provided
  const options = config.jwtToken ? {
    global: {
      headers: {
        Authorization: `Bearer ${config.jwtToken}`,
      },
    },
  } : {};
  
  _client = createClient(config.supabaseUrl, authKey, options);
  _currentConfig = config;

  // Decode JWT claims so customer ID is available for document operations
  setJWTClaims(config);

  return _client;
}

/**
 * Get the current Supabase client instance
 * Throws error if not initialized
 */
export function getSupabaseClient(): SupabaseClient {
  if (!_client) {
    throw new Error('Supabase client not initialized. Call initializeSupabase() first.');
  }
  return _client;
}

/**
 * Check if Supabase client is configured
 */
export function isConfigured(): boolean {
  return _client !== null;
}

/**
 * Get current configuration
 */
export function getCurrentConfig(): EDIConfig | null {
  return _currentConfig;
}

/**
 * Reset the Supabase client (useful for testing or re-authentication)
 */
export function resetSupabase(): void {
  _client = null;
  _currentConfig = null;
  console.log('🔄 Supabase client reset');
}

/**
 * Legacy export for backward compatibility
 * @deprecated Use getSupabaseClient() instead
 */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    if (!_client) {
      throw new Error('Supabase client not initialized. Call initializeSupabase() first.');
    }
    return (_client as any)[prop];
  },
});
